// client/src/pages/clientPortal/PortalRegisterPage.js — free website sign-up for the
// property search: saved searches, favourites and new matches. Uses the client-portal
// login (a 'visitor' account — property features only, never a client's files). The
// sign-up also becomes a lead for an agent (routes/clientPortal.js /register).
import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

const inputCls = 'mt-1 block w-full rounded-md border border-slate-400 px-3 py-2 text-slate-900 focus:border-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-600';

const safeNext = (n) => (n && /^\/(properties|client-portal)/.test(n) ? n : '/client-portal/my-properties');

const PortalRegisterPage = () => {
    const [sp] = useSearchParams();
    const navigate = useNavigate();
    const [f, setF] = useState({ name: '', email: '', phone: '', password: '', client_type: 'Buyer', website: '' });
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setError('');
        setBusy(true);
        try {
            const r = await fetch('/api/client-portal/register', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(f) });
            const j = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(j.message || 'Sign-up failed.');
            if (j.token) {
                localStorage.setItem('clientPortalToken', j.token);
                localStorage.setItem('clientPortalKind', j.accountKind || 'visitor');
            }
            navigate(safeNext(sp.get('next')));
        } catch (err) { setError(err.message); } finally { setBusy(false); }
    };

    return (
        <div className="flex min-h-screen items-center justify-center bg-slate-100 p-4">
            <main className="w-full max-w-md rounded-lg bg-white p-8 shadow">
                <h1 className="text-center text-2xl font-bold text-slate-900">Create your free account</h1>
                <p className="mt-2 text-center text-sm text-slate-700">Save searches, keep favourites and see new matches. An agent from our office may contact you to help — you can tell them if you would rather not.</p>
                <form onSubmit={submit} className="mt-6 space-y-4">
                    <div><label htmlFor="rg-name" className="block text-sm font-medium text-slate-800">Name</label>
                        <input id="rg-name" required autoComplete="name" className={inputCls} value={f.name} onChange={set('name')} /></div>
                    <div><label htmlFor="rg-email" className="block text-sm font-medium text-slate-800">Email</label>
                        <input id="rg-email" type="email" required autoComplete="email" className={inputCls} value={f.email} onChange={set('email')} /></div>
                    <div><label htmlFor="rg-phone" className="block text-sm font-medium text-slate-800">Phone (optional)</label>
                        <input id="rg-phone" type="tel" autoComplete="tel" className={inputCls} value={f.phone} onChange={set('phone')} /></div>
                    <div><label htmlFor="rg-type" className="block text-sm font-medium text-slate-800">I am looking to</label>
                        <select id="rg-type" className={inputCls} value={f.client_type} onChange={set('client_type')}>
                            <option value="Buyer">Buy</option><option value="Tenant">Rent or lease</option><option value="Seller">Sell</option><option value="Landlord">Lease out my property</option>
                        </select></div>
                    <div><label htmlFor="rg-pw" className="block text-sm font-medium text-slate-800">Password (at least 8 characters)</label>
                        <input id="rg-pw" type="password" required minLength={8} autoComplete="new-password" className={inputCls} value={f.password} onChange={set('password')} /></div>
                    <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px' }}>
                        <label htmlFor="rg-website">Website</label><input id="rg-website" tabIndex={-1} autoComplete="off" value={f.website} onChange={set('website')} />
                    </div>
                    {error && <p className="text-sm text-red-700" role="alert">{error}</p>}
                    <button type="submit" disabled={busy} className="w-full rounded-md bg-slate-900 px-4 py-3 font-semibold text-white hover:bg-slate-900 focus:outline-none focus:ring-4 focus:ring-indigo-300 disabled:opacity-60">
                        {busy ? 'Creating…' : 'Create account'}
                    </button>
                </form>
                <p className="mt-4 text-center text-sm text-slate-700">Already have an account? <Link className="font-semibold text-indigo-800 underline" to="/client-portal/login">Sign in</Link></p>
                <p className="mt-2 text-center text-sm"><Link className="text-indigo-800 underline" to="/properties">Back to properties</Link></p>
            </main>
        </div>
    );
};

export default PortalRegisterPage;
